// === SHARED: feedback ===
// A tiny, dependency-free TACTILE-feedback helper for the cross-window "remote"
// programs (PatchBay, Transmitter, PowerMonitor). The remote rigs are meant to
// feel like physical control surfaces, so every meaningful interaction should
// answer back with a short click/buzz under the finger.
//
// tactile({ kind, sound }) does two independent things:
//   1. HAPTIC — fires navigator.vibrate(...) with a short distinct pattern per
//      control kind, when the platform supports it. Strictly feature-guarded:
//      typeof navigator.vibrate === "function". Most desktops have no vibration
//      motor, so this is simply a no-op there — that's fine.
//   2. AUDIO — plays a very short, very quiet WebAudio blip (a click / detent /
//      beep, shaped per kind) through this module's OWN little AudioContext.
//      It deliberately does NOT touch the audioBus / music graph, so it never
//      claims, pauses, or ducks any playing source. The blip honors a global
//      mute: pass sound:false (threaded from the Annex's soundEnabled flag) and
//      the audio half is skipped — the haptic half may still fire.
//
// Design notes:
//  - No React, no DOM. One lazily-created AudioContext, reused across calls.
//  - Null-safe + fully guarded: in a non-browser / no-WebAudio environment this
//    degrades to a silent no-op and never throws.
//  - Blips are intentionally tiny (a few ms .. ~70ms) and low-gain so a fast
//    flurry of clicks stays subtle rather than turning into noise.

// ---- Haptic patterns (milliseconds), one per control kind. -----------------
// Kept short + distinct so the hand can tell a switch flip from a button press.
const HAPTIC_PATTERNS = {
  switch: [12],            // a single crisp throw
  detent: [6],             // a faint notch as a knob steps
  button: [16],            // a firmer press
  surge: [10, 30, 18],     // a rolling jolt for the one-shot surge
  load: [8, 20],           // a soft two-step chunk
  transmit: [14, 24, 14],  // a punchier launch
};

// ---- Audio shapes, one per control kind. -----------------------------------
// freq = tone in Hz, dur = length in seconds, gain = peak level (kept low),
// type = oscillator waveform. "square" reads as a harder click; "sine" softer.
const AUDIO_SHAPES = {
  switch: { freq: 320, dur: 0.022, gain: 0.05, type: "square" },
  detent: { freq: 540, dur: 0.014, gain: 0.035, type: "square" },
  button: { freq: 240, dur: 0.03, gain: 0.06, type: "square" },
  surge: { freq: 150, dur: 0.07, gain: 0.07, type: "sawtooth" },
  load: { freq: 300, dur: 0.028, gain: 0.05, type: "triangle" },
  transmit: { freq: 460, dur: 0.05, gain: 0.06, type: "sine" },
};

// One shared AudioContext for ALL blips, created lazily on first real use so we
// never spin one up just by importing this module. Separate from audioBus.
let _ctx = null;

function getCtx() {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (typeof Ctor !== "function") return null;
  try {
    if (_ctx == null) _ctx = new Ctor();
    // Some browsers start the context suspended until a user gesture. Every
    // tactile() call rides a real interaction, so a resume here is safe.
    if (_ctx.state === "suspended" && typeof _ctx.resume === "function") {
      _ctx.resume().catch(function () {});
    }
    return _ctx;
  } catch (e) {
    return null;
  }
}

// Fire the haptic motor for a kind, if the platform exposes one.
function buzz(kind) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // FEATURE-GUARD
  const pattern = HAPTIC_PATTERNS[kind] || HAPTIC_PATTERNS.button;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    /* ignore — vibration is best-effort */
  }
}

// Play one short, quiet blip for a kind through our own context.
function blip(kind) {
  const ctx = getCtx();
  if (!ctx) return;
  const shape = AUDIO_SHAPES[kind] || AUDIO_SHAPES.button;
  try {
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = shape.type;
    osc.frequency.setValueAtTime(shape.freq, now);
    // A quick attack + exponential decay reads as a tactile "click" rather than
    // a sustained tone. Ramp to a tiny floor (not 0) so the exp ramp is legal.
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(shape.gain, now + 0.004);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + shape.dur);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + shape.dur + 0.02);
    // Let the nodes be collected once they've finished sounding.
    osc.onended = function () {
      try {
        osc.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };
  } catch (e) {
    /* ignore — audio is best-effort */
  }
}

// ---- Public API ------------------------------------------------------------
// tactile({ kind, sound }) — give one tactile answer for a control interaction.
//   kind  : one of "switch" | "detent" | "button" | "surge" | "load" | "transmit"
//   sound : when false, SKIP the audio blip (global mute). Haptic still fires.
//           Defaults to true so callers that don't thread it still get audio.
export function tactile(opts) {
  const o = opts || {};
  const kind = typeof o.kind === "string" ? o.kind : "button";
  buzz(kind);
  if (o.sound !== false) blip(kind);
}

// ===========================================================================
// ADDITIVE: payoff({ kind, sound }) — a BIGGER, more spectacular cousin of
// tactile(), for the rare "ACTIVATION" moments (TRANSMIT / SURGE) that deserve
// a real reward rather than a click. Everything above is untouched.
//
// Where tactile() is a single short blip, payoff() fires:
//   1. HAPTIC — a longer, rolling vibration pattern (a felt "WHUMP" + tail),
//      still strictly feature-guarded so it's a silent no-op on most desktops.
//   2. AUDIO  — a layered ~0.9s burst: a falling/rising tone SWEEP, a low
//      sub "thud" body, and a short filtered-noise crack on top, so the ear
//      reads it as a powerful discharge rather than a beep. Still routed
//      through THIS module's own context (never the music graph) and still
//      honors the global mute via sound:false.
//
// It is the audio/haptic half of the visual eruption the PowerMonitor shows;
// callers pair it with a windowBus broadcast so every window erupts together.
// Like everything here it is fully guarded and degrades to a no-op.
// ---------------------------------------------------------------------------

// Longer rolling haptic patterns for the big moments. Distinct per kind so a
// transmit launch and a power surge still feel different in the hand.
const PAYOFF_HAPTICS = {
  surge: [22, 18, 40, 14, 70],      // stutter then a long discharge
  transmit: [30, 12, 26, 12, 60],   // a punchier multi-stage launch
};

// Per-kind shape for the layered burst. start/end = sweep endpoints in Hz.
const PAYOFF_BURSTS = {
  surge: { start: 220, end: 60, dur: 0.85, gain: 0.16, sub: 48, type: "sawtooth" },
  transmit: { start: 180, end: 760, dur: 0.8, gain: 0.15, sub: 70, type: "square" },
};

// Honor prefers-reduced-motion for the AUDIO intensity too: a calmer, shorter,
// quieter burst (still clearly "something big happened", just not a wall of it).
function prefersReducedMotion() {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch (e) {
    return false;
  }
}

// A short burst of band-limited noise — reads as the "crack" of a discharge.
function noiseCrack(ctx, now, gain, dur) {
  try {
    const frames = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) {
      // Decaying white noise so the crack has a sharp head and quick tail.
      data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(gain, now);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    // A high-pass keeps it a "tsss" crack rather than a muddy thump.
    let node = src;
    if (typeof ctx.createBiquadFilter === "function") {
      const hp = ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(1400, now);
      src.connect(hp);
      node = hp;
    }
    node.connect(amp);
    amp.connect(ctx.destination);
    src.start(now);
    src.stop(now + dur + 0.02);
    src.onended = function () {
      try {
        src.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };
  } catch (e) {
    /* ignore — the noise layer is best-effort */
  }
}

// Play the layered activation burst for a kind through our own context.
function burst(kind) {
  const ctx = getCtx();
  if (!ctx) return;
  const base = PAYOFF_BURSTS[kind] || PAYOFF_BURSTS.surge;
  const calm = prefersReducedMotion();
  // Reduced-motion: shorter + quieter, and we skip the abrasive noise crack.
  const dur = calm ? Math.min(base.dur, 0.4) : base.dur;
  const gain = calm ? base.gain * 0.5 : base.gain;
  try {
    const now = ctx.currentTime;

    // 1) The sweeping lead tone — the "voice" of the discharge.
    const osc = ctx.createOscillator();
    const amp = ctx.createGain();
    osc.type = base.type;
    osc.frequency.setValueAtTime(base.start, now);
    if (typeof osc.frequency.exponentialRampToValueAtTime === "function") {
      osc.frequency.exponentialRampToValueAtTime(Math.max(20, base.end), now + dur);
    } else {
      osc.frequency.linearRampToValueAtTime(Math.max(20, base.end), now + dur);
    }
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(gain, now + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(amp);
    amp.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + dur + 0.03);
    osc.onended = function () {
      try {
        osc.disconnect();
        amp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };

    // 2) A low sub "body" so the burst has weight on capable outputs.
    const sub = ctx.createOscillator();
    const subAmp = ctx.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(base.sub, now);
    sub.frequency.exponentialRampToValueAtTime(Math.max(20, base.sub * 0.5), now + dur * 0.8);
    subAmp.gain.setValueAtTime(0.0001, now);
    subAmp.gain.exponentialRampToValueAtTime(gain * 0.9, now + 0.02);
    subAmp.gain.exponentialRampToValueAtTime(0.0001, now + dur * 0.9);
    sub.connect(subAmp);
    subAmp.connect(ctx.destination);
    sub.start(now);
    sub.stop(now + dur + 0.03);
    sub.onended = function () {
      try {
        sub.disconnect();
        subAmp.disconnect();
      } catch (e) {
        /* ignore */
      }
    };

    // 3) The noise crack on top — skipped in the calmer reduced-motion mode.
    if (!calm) noiseCrack(ctx, now, gain * 0.8, 0.16);
  } catch (e) {
    /* ignore — audio is best-effort */
  }
}

// Fire the bigger rolling haptic, if the platform exposes a motor.
function bigBuzz(kind) {
  if (typeof navigator === "undefined") return;
  if (typeof navigator.vibrate !== "function") return; // FEATURE-GUARD
  const pattern = PAYOFF_HAPTICS[kind] || PAYOFF_HAPTICS.surge;
  try {
    navigator.vibrate(pattern);
  } catch (e) {
    /* ignore — vibration is best-effort */
  }
}

// payoff({ kind, sound }) — the spectacular activation feedback.
//   kind  : "surge" | "transmit" (falls back to "surge").
//   sound : when false, SKIP the audio burst (global mute). Haptic still fires.
export function payoff(opts) {
  const o = opts || {};
  const kind = typeof o.kind === "string" ? o.kind : "surge";
  bigBuzz(kind);
  if (o.sound !== false) burst(kind);
}

export default tactile;
